import { Paginated } from 'types/api';

import { instance } from 'utils/api';

// v2 wire: camelCase rows (legacy GQL was snake_case); status is the string
// enum 'draft' | 'sent' | 'new' (legacy numeric '1' | '2' | '3').
export type ReportStatus = 'draft' | 'sent' | 'new';

export type ReportRow = {
  id: number;
  appointmentId: number | null;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  dateOfBirth: string;
  dateReport: string;
  status: ReportStatus;
  doctorInfo?: string | null;
  signature?: string | null;
  lang?: string | null;
  report: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icd?: any;
  createdAt: string;
  updatedAt?: string;
};

export type ReportPayload = Partial<{
  appointmentId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  dateOfBirth: string;
  dateReport: string;
  status: ReportStatus;
  doctorInfo: string | null;
  signature: string | null;
  lang: string | null;
  report: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icd: any;
}>;

// GET /reports — `type` filters by report scope on the v2 BE:
// type=draft → appointmentId IS NULL (legacy "personal" reports),
// type=sent  → appointmentId IS NOT NULL (legacy "medicus" reports).
export const fetchReports = async (params: {
  page?: number;
  limit?: number;
  name?: string;
  startDate?: string;
  endDate?: string;
  type?: ReportStatus;
  status?: ReportStatus;
}): Promise<Paginated<ReportRow>> => {
  const res = await instance.get<Paginated<ReportRow>>('reports', { params });

  return res.data;
};

export const fetchReportById = async (
  id: number | string
): Promise<ReportRow> => {
  const res = await instance.get<ReportRow>(`reports/${id}`);

  return res.data;
};

export const createReport = async (
  payload: ReportPayload
): Promise<ReportRow> => {
  const res = await instance.post<ReportRow>('reports', payload);

  return res.data;
};

export const updateReport = async (
  id: number | string,
  payload: ReportPayload
): Promise<ReportRow> => {
  const res = await instance.put<ReportRow>(`reports/${id}`, payload);

  return res.data;
};

export type ReportFileRow = {
  id: number;
  file: string;
  note?: string | null;
  appointmentId: number;
  createdAt: string;
  updatedAt?: string;
};

export const fetchReportsByPatient = async (
  patientId: number | string,
  params: { page?: number; limit?: number }
): Promise<Paginated<ReportRow>> => {
  const res = await instance.get<Paginated<ReportRow>>(
    `reports/by-patient/${patientId}`,
    { params }
  );

  return res.data;
};

export const fetchReportFilesByPatient = async (
  patientId: number | string,
  params: { page?: number; limit?: number }
): Promise<Paginated<ReportFileRow>> => {
  const res = await instance.get<Paginated<ReportFileRow>>(
    `reports/files/by-patient/${patientId}`,
    { params }
  );

  return res.data;
};

// POST /reports/files — upsert a report-file row + flip appointment COMPLETED.
export const createReportFile = (payload: {
  appointmentId: number | string;
  file: string;
  note?: string;
}) =>
  instance.post('reports/files', {
    ...payload,
    appointmentId: Number(payload.appointmentId)
  });
